/**
 * CollapsibleSection — reusable collapsible panel for the admin dashboard.
 * Persists open/closed state to localStorage.
 *
 * Used by: All dashboard panels (ProjectPanel, FinancePanel, AnalyticsPanel, ClaudeUpdateLog)
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

const COLLAPSE_STORAGE_KEY = 'admin_dashboard_collapsed_v1'

export default function CollapsibleSection({
  id,
  title,
  subtitle,
  icon: Icon,
  badge,
  defaultOpen = true,
  children,
  className = '',
}) {
  const [open, setOpen] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || '{}')
      return id in stored ? stored[id] : defaultOpen
    } catch {
      return defaultOpen
    }
  })

  function toggle() {
    setOpen(prev => {
      const next = !prev
      try {
        const stored = JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || '{}')
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify({ ...stored, [id]: next }))
      } catch {
        /* ignored */
      }
      return next
    })
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 mb-5 overflow-hidden ${className}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
          <span className="font-semibold text-gray-900 text-sm">{title}</span>
          {badge && (
            <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
          {subtitle && <span className="text-xs text-gray-400 hidden sm:block">{subtitle}</span>}
        </div>
        <ChevronDown
          className="w-4 h-4 text-gray-400 transition-transform shrink-0"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          aria-hidden="true"
        />
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </div>
  )
}

/** Small inline accordion used inside panels for nested content. */
export function InfoAccordion({ label, badge, badgeColor, children }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-700">{label}</span>
          {badge && (
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${badgeColor}`}>
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && <div className="px-4 py-3 bg-white">{children}</div>}
    </div>
  )
}
